<script setup>
import { useUsersStore, useUserGroupsStore, useReservationsStore, useRestaurantsStore } from '@/stores'
import stanchion from '@/assets/stanchion-rope-stands-green.svg'
import PreferredIcon from '@core/components/PreferredIcon.vue'

const selectedMarketFavorites = ref('')

const restaurantsStore = useRestaurantsStore()
const usersStore = useUsersStore()

const { isShowingPreferredRequestInfoDialog, myUser } = storeToRefs(usersStore)
const {
  preferredRestaurants,
  viewingDetailsRestaurant,
  viewingDetailsRestaurantTab,
  citySelectorData,
} = storeToRefs(restaurantsStore)

onBeforeMount(async () => {
  // 1. Always fetch the list of cities for the dropdown first
  await restaurantsStore.getCitySelectorData()

  // 2. NOTE: Skip heavy getRestaurants() call - GoogleImage will use preferredRestaurants data

  // 3. Check if a city was previously selected and stored
  const savedCity = localStorage.getItem('selectedCity')
  if (savedCity) {
    // 4. If so, set the dropdown to that city...
    selectedMarketFavorites.value = savedCity

    // 5. ...and fetch the preferred restaurants for that specific city
    await restaurantsStore.getPreferredRestaurants(null, savedCity)
  } else {
    // 6. If no city was saved, fetch the default "All Cities" view
    await restaurantsStore.getPreferredRestaurants(null, null)
  }
})

watch(selectedMarketFavorites, async newCity => {
  localStorage.setItem('selectedCity', newCity || '')
  await restaurantsStore.getPreferredRestaurants(null, newCity || null)
})

const newDraft = async (market, restaurant) => {
  await usersStore.showLoadingOverlay()
  try {
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market })
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const showRestaurantDetails = async (marketName, restaurantName) => {
  // Try to find full restaurant data from main store (has acceptingReferrals, etc.)
  const { restaurants } = storeToRefs(restaurantsStore)
  let fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)

  if (!fullRestaurant) {
    // Load restaurants store if not already loaded to get complete data
    await restaurantsStore.getRestaurants(null, false, marketName)
    fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)
  }

  if (fullRestaurant) {
    viewingDetailsRestaurant.value = fullRestaurant
  } else {
    // Fallback to preferredRestaurants if not found in main store
    let preferredRestaurant = preferredRestaurants.value.find(r => r.name == restaurantName && r.market == marketName)
    if (preferredRestaurant) {
      viewingDetailsRestaurant.value = preferredRestaurant
    }
  }
  viewingDetailsRestaurantTab.value = 'Details'
}

const showRestaurantSpaces = async (marketName, restaurantName) => {
  const { restaurants } = storeToRefs(restaurantsStore)
  let fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)

  if (!fullRestaurant) {
    await restaurantsStore.getRestaurants(null, false, marketName)
    fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)
  }

  if (fullRestaurant) {
    viewingDetailsRestaurant.value = fullRestaurant
  } else {
    let preferredRestaurant = preferredRestaurants.value.find(r => r.name == restaurantName && r.market == marketName)
    if (preferredRestaurant) {
      viewingDetailsRestaurant.value = preferredRestaurant
    }
  }
  viewingDetailsRestaurantTab.value = 'Events'
}

const showRestaurantHappenings = async (marketName, restaurantName) => {
  const { restaurants } = storeToRefs(restaurantsStore)
  let fullRestaurant = restaurants.value?.find(r => (r.name == restaurantName && r.market == marketName) || (r.confirmedRestaurant == restaurantName && r.market == marketName))

  if (!fullRestaurant) {
    await restaurantsStore.getRestaurants(null, false, marketName)
    fullRestaurant = restaurants.value?.find(r => (r.name == restaurantName && r.market == marketName) || (r.confirmedRestaurant == restaurantName && r.market == marketName))
  }

  if (fullRestaurant) {
    viewingDetailsRestaurant.value = fullRestaurant
  } else {
    let preferredRestaurant = preferredRestaurants.value.find(r => (r.name == restaurantName && r.market == marketName) || (r.confirmedRestaurant == restaurantName && r.market == marketName))
    if (preferredRestaurant) {
      viewingDetailsRestaurant.value = preferredRestaurant
    }
  }
  viewingDetailsRestaurantTab.value = 'Happenings'
}

const selectableMarketsFavorites = computed(() => {
  return citySelectorData.value?.cities || []
})

const filteredFavorites = computed(() => {
  if (preferredRestaurants.value) {
    return [...preferredRestaurants.value]
  } else {
    return []
  }
})

const backHome = computed(() => {
  if (myUser?.value && myUser?.value?.userLevel != 'dummy') {
    return '/discover'
  }
  
  return '/home/'
})
</script>

<template>
  <VContainer fluid>
    <div
      class="d-flex flex-column"
      :class="{
        'justify-center align-start': !$vuetify.display.smAndDown ,
        'justify-center align-center': $vuetify.display.smAndDown
      }"
    >
      <div
        v-if="!$vuetify.display.smAndDown"
        class="d-flex align-center mb-4 w-100"
      >
        <RouterLink :to="backHome">
          <VIcon
            color="secondary"
            size="x-large"
            class="mr-4"
          >
            mdi-chevron-left
          </VIcon>
        </RouterLink>
        <h1 class="text-center text-lg">
          Preferred
        </h1>
      </div>
      <div
        v-else
        class="d-flex justify-center align-center mb-4 w-100"
      >
        <p class="dark-text text-center text-xs">
          <PreferredIcon
            :size="16"
            style="margin-left: -3px; margin-right: 2px; display: inline-block; vertical-align: middle;"
          />

          <span style="margin-left: -4px;">:</span>
          Members' favorites that respond to reservation & private dining requests within 24 hours
        </p>
      </div>
      <VAutocomplete
        v-model="selectedMarketFavorites"
        label="City"
        :messages="!$vuetify.display.smAndDown ? 'Select a city' : ''"
        :items="selectableMarketsFavorites"
        :menu-props="{density: 'compact', height:'100%', width: !$vuetify.display.smAndDown ? 'auto' : '50%', minHeight: '50%' }"
        item-title="text"
        item-value="value"
        no-data-text="No Matches"
        clearable
        clear-on-select
        density="compact"
        :width="!$vuetify.display.smAndDown ? '50%' : '100%'"
      >
        <template
          v-if="!$vuetify.display.smAndDown"
          #message
        >
          <span class="dark-text d-flex">
            <VIcon style="color: rgb(var(--v-theme-primary)); margin-left: -3px; margin-right: 2px;">
              iconamoon:certificate-badge-light
            </VIcon>

            <span>
              <span style="margin-left: -3px;">:</span>
              Members' favorites that respond to reservation & private dining requests within 24 hours
            </span>
          </span>
        </template>
      </VAutocomplete>
    </div>
    <VRow
      v-if="!$vuetify.display.smAndDown"
      justify="center"
    >
      <VVirtualScroll
        :items="filteredFavorites"
        max-height="600"
        item-height="98"
      >
        <template #default="{ item, index }">
          <VListItem @click="showRestaurantDetails(item.market, item.name)">
            <div class="d-flex flex-row justify-start align-center">
              <div class="restaurant-image-holder--mobile flex-shrink-0">
                <GoogleImage
                  class="restaurant-image"
                  :restaurant="item"
                />
              </div>
              <div class="pa-4">
                <VListItemTitle>
                  <div class="d-flex flex-wrap align-items-center">
                    <div>
                      <PreferredIcon
                        v-if="item.status === 'Preferred'"
                        :size="20"
                      />
                      <span
                        class="font-weight-bold text-truncate"
                        :style="(item.status || item.raw.status) === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: 24px;'"
                      >
                        {{ item.name }}
                      </span>
                      <span class="trending-name mt-1 ml-1">
                        <RestaurantHeart
                          :restaurant-name="item.name"
                          :restaurant-market="item.market"
                        />
                      </span>
                    </div>
                  </div>
                </VListItemTitle>
                <VListItemSubtitle :style="(item.status || item.raw.status) === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'">
                  <span
                    v-if="item.group"
                    class="trending-company text-xs"
                  >{{ item.group }}</span>
                  <span class="trending-company text-xs">{{ item.market }}</span>
                  <div class="d-flex align-center">
                    <div
                      class="d-flex cursor-pointer"
                      @click.stop="showRestaurantSpaces(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                    >
                      <img
                        v-if="item?.eventSpaces?.length > 0"
                        :src="stanchion"
                        height="16"
                        class="align-self-center"
                      >
                      <span
                        v-if="item?.eventSpaces?.length > 0"
                        class="text-xs ml-1"
                      >
                        {{ item?.eventSpaces?.length }}
                      </span>
                    </div>
                    <VIcon
                      v-if="item?.showHappeningsIcon"
                      size="16"
                      class="align-self-center cursor-pointer"
                      @click.stop="showRestaurantHappenings(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                    >
                      emojione-v1:lightning-mood
                    </VIcon>
                  </div>
                </VListItemSubtitle>
              </div>
            </div>
            <template #append>
              <VBtn
                size="small"
                density="compact"
                color="primary"
                style="padding-left: 5px !important; padding-right: 5px !important;"
                icon="mdi-plus"
                @click.stop="newDraft(item.market, item.name);"
              />
            </template>
          </VListItem>
          <VDivider inset />
        </template>
      </VVirtualScroll>
    </VRow>
    <VRow v-else>
      <VCol
        cols="12"
        class="trending-wrapper"
      >
        <VVirtualScroll
          :items="filteredFavorites"
          max-height="600"
          item-height="98"
        >
          <template #default="{ item, index }">
            <VListItem @click="showRestaurantDetails(item.market, item.name)">
              <div class="d-flex align-center">
                <div class="restaurant-image-holder--mobile flex-shrink-0">
                  <GoogleImage
                    class="restaurant-image"
                    :restaurant="item"
                  />
                </div>
                <div class="py-4 pl-4">
                  <VListItemTitle>
                    <div class="d-flex flex-wrap justify-start align-items-center">
                      <div class="d-flex align-start">
                        <PreferredIcon
                          v-if="item.status === 'Preferred'"
                          :size="20"
                        />
                        <span
                          class="text-truncate"
                          :style="item.status === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: 24px;'"
                          style="max-width: 150px;"
                        >
                          <span class="font-weight-bold">
                            {{ item.name }}
                          </span>
                        </span>
                        <span class="trending-name mt-1">
                          <RestaurantHeart
                            :restaurant-name="item.name"
                            :restaurant-market="item.market"
                          />
                        </span>
                      </div>
                    </div>
                  </VListItemTitle>
                  <VListItemSubtitle :style="item.status === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'">
                    <div class="content">
                      <span
                        v-if="item.group"
                        class="trending-company text-xs"
                      >{{ item.group }}</span>
                      <span class="trending-company text-xs">{{ item.market }}</span>
                      <div class="d-flex align-center">
                        <div
                          class="d-flex cursor-pointer"
                          @click.stop="showRestaurantSpaces(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                        >
                          <img
                            v-if="item?.eventSpaces?.length > 0"
                            :src="stanchion"
                            height="16"
                            class="align-self-center"
                          >
                          <span
                            v-if="item?.eventSpaces?.length > 0"
                            class="text-xs ml-1"
                          >
                            {{ item?.eventSpaces?.length }}
                          </span>
                        </div>
                        <VIcon
                          v-if="item?.showHappeningsIcon"
                          size="16"
                          class="align-self-center cursor-pointer"
                          @click.stop="showRestaurantHappenings(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                        >
                          emojione-v1:lightning-mood
                        </VIcon>
                      </div>
                    </div>
                  </VListItemSubtitle>
                </div>
              </div>
              <template #append>
                <VBtn
                  size="small"
                  density="compact"
                  color="primary"
                  style="padding-left: 5px !important; padding-right: 5px !important;"
                  icon="mdi-plus"
                  @click.stop="newDraft(item.market, item.name);"
                />
              </template>
            </VListItem>
            <VDivider inset />
          </template>
        </VVirtualScroll>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>
.card-fixed-size {
  width: 300px; /* Fixed width */
  height: 270px; /* Fixed height */
  flex-shrink: 0; /* Prevents cards from shrinking */
}

.card-sm-size {
  width: 300px; /* Smaller width for small screens */
  height: 280px; /* Smaller height */
  flex-shrink: 0;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none; /* Prevent dragging images */
}

.restaurant-image:hover.lower-on-hover {
  width: 90%;
  height: 90%;
}

.restaurant-image-holder {
  width: 100%;
  height: 50%;
}

.restaurant-image-holder--mobile {
    width: 4rem;
    height: 4rem;
}

.dark-text {
  color: black !important;
  font-weight: 400 !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
